import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Lock, Minus, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAddTimeEntry } from '@/api/hooks';
import { cn } from '@/lib/utils';

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

// Manual adjustment snaps to 5-minute blocks (the smallest we bill in).
const STEP_MINUTES = 5;

// Autotask's smallest unit: a sub-minute session still logs as one minute rather than a
// fraction that rounds to nothing.
const MIN_SECONDS = 60;

/** First + press: the next 5-minute block above the running time (0m45s -> 5m, 7m -> 10m). */
function blockAbove(seconds: number): number {
  return Math.max(STEP_MINUTES, (Math.floor(seconds / 60 / STEP_MINUTES) + 1) * STEP_MINUTES);
}

/** First - press: the nearest 5-minute block below it, never under one block. */
function blockBelow(seconds: number): number {
  return Math.max(STEP_MINUTES, (Math.ceil(seconds / 60 / STEP_MINUTES) - 1) * STEP_MINUTES);
}

interface TimeEntryFormProps {
  ticketId: number;
  startTime: Date;
  /** 'plain' drops the card chrome — used when the form sits inside the Log Time modal. */
  variant?: 'card' | 'plain';
  /** Called after a successful submit (e.g. to close the modal). */
  onLogged?: () => void;
  /**
   * Who the summary reaches, for the confirmation step — e.g. "Jane Doe and 1 other".
   * Only affects wording; the note is customer-visible either way.
   */
  customerAudience?: string | null;
}

export function TimeEntryForm({ ticketId, startTime, variant = 'card', onLogged, customerAudience }: TimeEntryFormProps) {
  const [sessionStart, setSessionStart] = useState(startTime);
  const [elapsed, setElapsed] = useState(0);
  const [adjustedMinutes, setAdjustedMinutes] = useState<number | null>(null);
  const [summary, setSummary] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  // Armed by the first click when there's a summary; the second click actually logs it.
  const [confirming, setConfirming] = useState(false);
  const mutation = useAddTimeEntry();

  useEffect(() => {
    const tick = () => setElapsed(Math.floor((Date.now() - sessionStart.getTime()) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionStart]);

  const isLive = adjustedMinutes === null;
  // Live mode logs the exact running time (start = now − elapsed, end = now), floored at one
  // minute. The +/- buttons switch to manual 5-minute blocks; only then is it snapped.
  const effectiveSeconds = isLive ? Math.max(MIN_SECONDS, elapsed) : adjustedMinutes * 60;
  const hoursWorked = effectiveSeconds / 3600;
  // Built from effectiveSeconds, not raw elapsed, so the figure shown is the figure logged.
  const durationLabel = isLive ? formatElapsed(effectiveSeconds) : formatMinutes(adjustedMinutes);

  const adjust = (delta: number) => {
    // From live, the first press snaps to the adjacent block rather than stepping past it —
    // otherwise + on a 45-second session jumped straight to 10m.
    if (adjustedMinutes === null) {
      setAdjustedMinutes(delta > 0 ? blockAbove(elapsed) : blockBelow(elapsed));
      return;
    }
    setAdjustedMinutes(Math.max(STEP_MINUTES, adjustedMinutes + delta));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!summary.trim() && !internalNotes.trim()) {
      toast.error('Add a summary or internal notes');
      return;
    }
    // The summary is posted as a customer-visible note, so it takes one deliberate extra
    // click. An internal-only entry never reaches the customer and logs straight away.
    if (summary.trim() && !confirming) {
      setConfirming(true);
      return;
    }

    const endDateTime = new Date();
    const startDateTime = new Date(endDateTime.getTime() - effectiveSeconds * 1000);

    mutation.mutate(
      {
        id: ticketId,
        body: {
          hoursWorked,
          summaryNotes: summary.trim() || undefined,
          internalNotes: internalNotes.trim() || undefined,
          startDateTime: startDateTime.toISOString(),
          endDateTime: endDateTime.toISOString(),
        },
      },
      {
        onSuccess: (data) => {
          const sentSummary = Boolean(summary.trim());
          setSummary('');
          setConfirming(false);
          setInternalNotes('');
          setAdjustedMinutes(null);
          setSessionStart(new Date());
          if (data?.noteError) {
            // Time landed, the customer note didn't — say so instead of a silent success,
            // so the summary can be re-posted from the note form.
            toast.warning(`Logged ${durationLabel}, but the customer note failed`, {
              description: `${data.noteError} — re-post the summary from Add note.`,
            });
            onLogged?.();
            return;
          }
          toast.success(`Logged ${durationLabel}`, {
            description: sentSummary ? 'Summary sent to the customer as a note' : 'Internal only — nothing sent to the customer',
          });
          onLogged?.();
        },
        onError: (err) => {
          setConfirming(false);
          toast.error('Failed to log time', { description: err instanceof Error ? err.message : 'Unknown error' });
        },
      },
    );
  };

  return (
    <form
      onSubmit={submit}
      // Inside the Log Time modal, Escape would close the whole dialog. While the
      // confirmation is armed it should back that out first; a second Escape closes.
      onKeyDown={(e) => {
        if (e.key === 'Escape' && confirming) {
          e.stopPropagation();
          setConfirming(false);
        }
      }}
      className={cn('space-y-3', variant === 'card' && 'rounded-md border bg-card p-3')}
    >
      {/* Timer row */}
      <div className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
        <div className="flex flex-col">
          <span className="text-[11px] text-muted-foreground">
            {isLive ? 'Running' : 'Adjusted'}
          </span>
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {formatElapsed(elapsed)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => adjust(-STEP_MINUTES)}
            disabled={!isLive && adjustedMinutes <= STEP_MINUTES}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
            aria-label="Remove 5 minutes"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[52px] text-center text-[15px] font-semibold tabular-nums">
            {durationLabel}
          </span>
          <button
            type="button"
            onClick={() => adjust(STEP_MINUTES)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Add 5 minutes"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Users className="h-3.5 w-3.5 text-orange-500"/>
            Summary
            <span className="font-normal text-orange-600 dark:text-orange-400">→ sent to the customer</span>
          </label>
          <Textarea
            value={summary}
            // Editing the summary after arming disarms it — the customer-facing text changed.
            onChange={(e) => { setSummary(e.target.value); setConfirming(false); }}
            placeholder="What should the customer be told? Posted as a customer-visible note…"
            rows={2}
          />
        </div>
        <div>
          <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Lock className="h-3.5 w-3.5"/>
            Internal Notes
            <span className="font-normal">→ timesheet only</span>
          </label>
          <Textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Work detail for the timesheet (never seen by the customer)…"
            rows={2}
          />
        </div>
      </div>

      {confirming && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            The summary will be posted as a customer-visible note to{' '}
            <span className="font-medium">{customerAudience || 'the customer'}</span>. Clear it to
            log this time internally only.
          </span>
        </div>
      )}

      <div className="flex justify-end gap-1.5">
        {confirming ? (
          <>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" autoFocus disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Log Time &amp; send
            </Button>
          </>
        ) : (
          <Button type="submit" size="sm" disabled={mutation.isPending || (!summary.trim() && !internalNotes.trim())}>
            {mutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Log Time
          </Button>
        )}
      </div>
    </form>
  );
}
